import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { QRCodeCanvas } from 'qrcode.react';
import './Dashboard.css';

const Solicitudes = () => {
  const navigate = useNavigate();
  const [solicitudes, setSolicitudes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showQrModal, setShowQrModal] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get('tab') || 'pendientes';
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = currentUser.rol?.toLowerCase() || '';

  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBase = isLocal ? `http://${window.location.hostname}:3010` : `${window.location.protocol}//${window.location.hostname}`;

  useEffect(() => {
    if (userRole !== 'admin' && userRole !== 'superadmin' && userRole !== 'jefatura') {
      navigate('/dashboard');
    }
    fetchSolicitudes();

    const interval = setInterval(() => {
      fetchSolicitudes();
    }, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchSolicitudes = async () => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/solicitudes`, { credentials: 'include', 
        headers: {  }
      });
      if (resp.ok) setSolicitudes(await resp.json());
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (id, nuevoEstado, confirmMsg) => {
    if (!window.confirm(confirmMsg || `¿Estás seguro de marcar esta solicitud como ${nuevoEstado}?`)) return;
    try {
      const resp = await fetch(`${apiBase}/api/solicitudes/${id}`, { credentials: 'include', 
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify({ nuevoEstado })
      });
      if (resp.ok) {
        fetchSolicitudes();
      }
    } catch (err) { console.error(err); }
  };

  const downloadQR = (id = "qr-solicitudes") => {
    const canvas = document.getElementById(id);
    if (!canvas) return;
    const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
    let downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = "QR_SOLICITUDES_MUNICIPALES.png";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const TIPOS = ['Nuevo Equipo', 'Traslado', 'Préstamo', 'Extravío', 'Sustracción'];

  const getFilteredSolicitudes = () => {
    let base = [];
    if (activeTab === 'pendientes') {
      if (userRole === 'admin' || userRole === 'superadmin') {
        base = solicitudes.filter(s => s.estado === 'Pendiente' || s.estado === 'Aprobada por Jefatura');
      } else if (userRole === 'jefatura') {
        base = solicitudes.filter(s => s.estado === 'Pendiente');
      } else {
        base = solicitudes.filter(s => s.estado === 'Aprobada por Jefatura');
      }
    } else {
      if (userRole === 'admin' || userRole === 'superadmin') {
        base = solicitudes.filter(s => s.estado !== 'Pendiente' && s.estado !== 'Aprobada por Jefatura');
      } else if (userRole === 'jefatura') {
        base = solicitudes.filter(s => s.estado !== 'Pendiente');
      } else {
        base = solicitudes.filter(s => s.estado !== 'Aprobada por Jefatura');
      }
    }

    // Aplicar filtros adicionales
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      base = base.filter(s =>
        s.usuario_nombre?.toLowerCase().includes(term) ||
        s.usuario_rut?.toLowerCase().includes(term) ||
        s.equipo_solicitado?.toLowerCase().includes(term) ||
        s.motivo?.toLowerCase().includes(term)
      );
    }
    if (filtroTipo) {
      base = base.filter(s => {
        const tipoActual = s.tipo_solicitud === 'Requerimiento' ? 'Nuevo Equipo' : (s.tipo_solicitud || 'Nuevo Equipo');
        return tipoActual === filtroTipo;
      });
    }
    if (filtroEstado) {
      base = base.filter(s => s.estado === filtroEstado);
    }

    return base;
  };

  const filtered = getFilteredSolicitudes();

  // Contadores para chips de resumen
  const countByEstado = (estado) => solicitudes.filter(s => s.estado === estado).length;
  const countPendiente = countByEstado('Pendiente');
  const countAprobada = countByEstado('Aprobada por Jefatura');
  const countPendienteFirma = countByEstado('Pendiente de Firma');
  const countFinalizada = countByEstado('Finalizada');
  const countRechazada = countByEstado('Rechazada');

  const getEstadoBorderColor = (estado) => {
    switch(estado) {
      case 'Pendiente': return '#f59e0b';
      case 'Aprobada por Jefatura': return '#10b981';
      case 'Pendiente de Firma': return '#3b82f6';
      case 'Finalizada': return '#6366f1';
      case 'Rechazada': return '#f43f5e';
      default: return 'var(--glass-border)';
    }
  };

  const truncateEquipo = (text, max = 45) => {
    if (!text) return '';
    if (text.length <= max) return text;
    return text.substring(0, max) + '…';
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />

      <main className="main-content">
        <header className="content-header">
          <div className="header-title">
            <h1>Bandeja de Solicitudes</h1>
          </div>
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <button
              className={`action-button-glass ${isLoading ? 'loading-spin' : ''}`}
              onClick={fetchSolicitudes}
              disabled={isLoading}
              title="Actualizar Solicitudes"
              style={{ width: '45px', height: '45px', borderRadius: '12px' }}
            >
              <i className={`fa-solid fa-arrows-rotate ${isLoading ? 'fa-spin' : ''}`}></i>
            </button>

            <div className="qr-container-premium">
              <div className="qr-box" onClick={() => setShowQrModal(true)} title="Click para ampliar QR">
                <QRCodeCanvas
                  id="qr-solicitudes"
                  value={`${window.location.origin}/solicitar`}
                  size={60}
                  level={"H"}
                  includeMargin={true}
                  style={{ borderRadius: '6px' }}
                />
                <div className="qr-overlay">
                  <i className="fa-solid fa-expand"></i>
                </div>
              </div>
              <div className="qr-info" style={{ display: 'none' }}>
                <span className="qr-label">QR OFICIAL</span>
              </div>
            </div>

            <Link to="/perfil" className="user-profile">
              <span>{currentUser.nombre}</span>
              <div className="avatar">{currentUser.nombre?.charAt(0)}</div>
            </Link>
          </div>
        </header>

        {/* Mini-Widgets de Resumen */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '25px', flexWrap: 'wrap' }}>
          {[
            { label: 'Pendientes', count: countPendiente, color: '#f59e0b', icon: 'fa-clock', filter: 'Pendiente' },
            { label: 'Aprobadas', count: countAprobada, color: '#10b981', icon: 'fa-check-circle', filter: 'Aprobada por Jefatura' },
            { label: 'Firma Pendiente', count: countPendienteFirma, color: '#3b82f6', icon: 'fa-file-signature', filter: 'Pendiente de Firma' },
            { label: 'Finalizadas', count: countFinalizada, color: '#6366f1', icon: 'fa-flag-checkered', filter: 'Finalizada' },
            { label: 'Rechazadas', count: countRechazada, color: '#f43f5e', icon: 'fa-ban', filter: 'Rechazada' },
          ].map(chip => (
            <button
              key={chip.label}
              onClick={() => setFiltroEstado(filtroEstado === chip.filter ? '' : chip.filter)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 18px',
                borderRadius: '14px',
                border: filtroEstado === chip.filter ? `2px solid ${chip.color}` : '1px solid var(--glass-border)',
                background: filtroEstado === chip.filter ? `${chip.color}15` : 'var(--card-bg)',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                transform: filtroEstado === chip.filter ? 'scale(1.05)' : 'scale(1)',
                boxShadow: filtroEstado === chip.filter ? `0 6px 20px ${chip.color}30` : 'none'
              }}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '10px',
                background: `${chip.color}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: chip.color,
                fontSize: '14px'
              }}>
                <i className={`fa-solid ${chip.icon}`}></i>
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '20px', fontWeight: '800', color: chip.color, lineHeight: 1 }}>{chip.count}</div>
                <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--p-slate)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{chip.label}</div>
              </div>
            </button>
          ))}
        </div>

        <section className="datatable-section animate-fade-in" style={{ marginTop: 0 }}>
          <div className="datatable-header" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '25px',
            paddingBottom: '15px',
            borderBottom: '1px solid var(--glass-border)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '36px', height: '36px', borderRadius: '10px',
                background: 'linear-gradient(135deg, var(--p-indigo), var(--p-blue))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: '16px'
              }}>
                <i className="fa-solid fa-layer-group"></i>
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px' }}>
                  {filtroEstado || 'Todas las Solicitudes'}
                </h2>
                <span style={{ fontSize: '12px', color: 'var(--p-slate)', fontWeight: '600' }}>
                  {filtered.length} {filtered.length === 1 ? 'resultado' : 'resultados'}
                </span>
              </div>
            </div>
            
            <div className="header-actions" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <div className="search-bar" style={{ margin: 0, position: 'relative' }}>
                <i className="fa-solid fa-magnifying-glass" style={{ 
                  position: 'absolute', 
                  marginLeft: '15px', 
                  marginTop: '16px', 
                  color: 'var(--p-slate)' 
                }}></i>
                <input
                  type="text"
                  placeholder="Buscar solicitud..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    paddingLeft: '40px', 
                    width: '250px',
                    height: '45px',
                    background: 'var(--input-bg)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: '12px',
                    color: 'var(--p-navy)',
                    fontSize: '14px'
                  }}
                />
              </div>

              <select
                className="select-premium"
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                style={{ width: '180px', height: '45px' }}
              >
                <option value="">Todos los Tipos</option>
                {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>

              {(searchTerm || filtroTipo || filtroEstado) && (
                <button
                  className="btn-secondary"
                  onClick={() => { setSearchTerm(''); setFiltroTipo(''); setFiltroEstado(''); }}
                  style={{ height: '45px', padding: '0 15px' }}
                  title="Limpiar todos los filtros"
                >
                  <i className="fa-solid fa-eraser"></i>
                </button>
              )}
            </div>
          </div>

          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                   <th style={{ width: '4px', padding: 0 }}></th>
                   <th>Fecha</th>
                   <th>Solicitante</th>
                   <th>Tipo</th>
                   <th>Equipo</th>
                   <th>Estado</th>
                   <th style={{ textAlign: 'right' }}>Acciones</th>
                 </tr>
               </thead>
               <tbody>
                 {isLoading && filtered.length === 0 ? (
                   <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>Cargando solicitudes...</td></tr>
                 ) : filtered.length === 0 ? (
                   <tr><td colSpan="7" style={{ textAlign: 'center', padding: '60px', color: 'var(--p-slate)' }}>
                     <i className="fa-solid fa-inbox" style={{ fontSize: '40px', marginBottom: '15px', display: 'block', opacity: 0.4 }}></i>
                     No hay solicitudes que coincidan con los filtros.
                   </td></tr>
                 ) : filtered.map(s => (
                   <tr key={s.id} style={{ 
                     background: s.estado === 'Pendiente' ? 'rgba(245, 158, 11, 0.03)' : 'transparent',
                     cursor: 'pointer'
                   }} onClick={() => setSelectedSolicitud(s)}>
                     {/* Borde lateral de color según estado */}
                     <td style={{ 
                       padding: 0, width: '4px', 
                       background: getEstadoBorderColor(s.estado),
                       borderBottom: '1px solid var(--glass-border)'
                     }}></td>
                     <td data-label="Fecha">
                       <div style={{ fontWeight: '600' }}>{new Date(s.creado_en).toLocaleDateString('es-CL')}</div>
                       <div style={{ fontSize: '10px', color: 'var(--p-slate)' }}>
                         {new Date(s.creado_en).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                       </div>
                     </td>
                     <td data-label="Solicitante">
                       <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                         <div style={{ 
                           width: '34px', height: '34px', borderRadius: '10px',
                           background: `${getEstadoBorderColor(s.estado)}18`,
                           display: 'flex', alignItems: 'center', justifyContent: 'center',
                           color: getEstadoBorderColor(s.estado), fontWeight: '800', fontSize: '14px',
                           flexShrink: 0
                         }}>
                           {s.usuario_nombre?.charAt(0)}
                         </div>
                         <div>
                           <strong style={{ fontSize: '14px' }}>{s.usuario_nombre}</strong>
                           <div style={{ fontSize: '11px', color: 'var(--p-slate)' }}>{s.usuario_rut}</div>
                         </div>
                       </div>
                     </td>
                     <td data-label="Tipo">
                       <div style={{ 
                         display: 'inline-flex', alignItems: 'center', gap: '8px',
                         background: s.tipo_solicitud === 'Extravío' || s.tipo_solicitud === 'Sustracción' ? 'rgba(239, 68, 68, 0.1)' : 
                                     s.tipo_solicitud === 'Traslado' ? 'rgba(59, 130, 246, 0.1)' :
                                     s.tipo_solicitud === 'Préstamo' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                         color: s.tipo_solicitud === 'Extravío' || s.tipo_solicitud === 'Sustracción' ? 'var(--p-rose)' : 
                                s.tipo_solicitud === 'Traslado' ? 'var(--p-blue)' :
                                s.tipo_solicitud === 'Préstamo' ? 'var(--p-amber)' : 'var(--p-indigo)',
                         padding: '8px 14px',
                         borderRadius: '10px',
                         fontSize: '12px',
                         fontWeight: '700',
                         border: `1px solid ${
                           s.tipo_solicitud === 'Extravío' || s.tipo_solicitud === 'Sustracción' ? 'rgba(239, 68, 68, 0.2)' : 
                           s.tipo_solicitud === 'Traslado' ? 'rgba(59, 130, 246, 0.2)' :
                           s.tipo_solicitud === 'Préstamo' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(99, 102, 241, 0.2)'
                         }`
                       }}>
                         <i className={`fa-solid ${
                           s.tipo_solicitud === 'Extravío' ? 'fa-magnifying-glass-minus' : 
                           s.tipo_solicitud === 'Sustracción' ? 'fa-mask' :
                           s.tipo_solicitud === 'Traslado' ? 'fa-truck-fast' :
                           s.tipo_solicitud === 'Préstamo' ? 'fa-calendar-day' : 'fa-plus'
                         }`}></i>
                         {(s.tipo_solicitud === 'Requerimiento' ? 'Nuevo Equipo' : (s.tipo_solicitud || 'Nuevo Equipo'))}
                       </div>
                     </td>
                     <td data-label="Equipo">
                       <div style={{ color: 'var(--p-navy)', fontWeight: '600', fontSize: '13px' }} title={s.equipo_solicitado}>
                         {truncateEquipo(s.equipo_solicitado)}
                       </div>
                     </td>
                     <td data-label="Estado">
                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         {s.estado === 'Pendiente' && (
                           <span style={{
                             width: '8px', height: '8px', borderRadius: '50%',
                             background: '#f59e0b',
                             boxShadow: '0 0 0 3px rgba(245, 158, 11, 0.2)',
                             animation: 'pulseGlow 2s ease-in-out infinite',
                             flexShrink: 0
                           }}></span>
                         )}
                         <span style={{ 
                           padding: '6px 14px',
                           borderRadius: '30px',
                           fontSize: '11px',
                           fontWeight: '700',
                           background: s.estado === 'Pendiente' ? 'rgba(245, 158, 11, 0.12)' :
                                       s.estado === 'Aprobada por Jefatura' ? 'rgba(16, 185, 129, 0.12)' :
                                       s.estado === 'Pendiente de Firma' ? 'rgba(59, 130, 246, 0.12)' :
                                       s.estado === 'Finalizada' ? 'rgba(99, 102, 241, 0.12)' : 'rgba(244, 63, 94, 0.12)',
                           color: getEstadoBorderColor(s.estado),
                           border: `1px solid ${getEstadoBorderColor(s.estado)}30`
                         }}>
                           {s.estado}
                         </span>
                       </div>
                     </td>
                     <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                       <div className="table-actions" style={{ justifyContent: 'flex-end' }}>
                         <button 
                           className="btn-primary" 
                           onClick={() => setSelectedSolicitud(s)} 
                           title="Ver Detalle"
                           style={{ 
                             padding: '8px 16px', fontSize: '12px', fontWeight: '700',
                             borderRadius: '10px', height: '36px'
                           }}
                         >
                           <i className="fa-solid fa-eye"></i> Ver
                         </button>
                       </div>
                     </td>
                   </tr>
                 ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {showQrModal && (
        <div className="modal-overlay" onClick={() => setShowQrModal(false)}>
          <div className="modal-content premium-modal qr-zoom-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'center', width: '100%', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--p-navy)', margin: 0, textAlign: 'center', width: '100%' }}>QR Oficial de Solicitudes</h3>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', paddingTop: '10px' }}>
              <div style={{ background: 'white', padding: '20px', borderRadius: '24px', display: 'inline-block', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', marginBottom: '30px' }}>
                <QRCodeCanvas
                  id="qr-solicitudes-large"
                  value={`${window.location.origin}/solicitar`}
                  size={300}
                  level={"H"}
                  includeMargin={true}
                />
              </div>
              <p style={{ color: 'var(--p-slate)', marginBottom: '30px', fontSize: '15px' }}>
                Este c&oacute;digo QR permite a cualquier funcionario acceder al formulario de solicitud desde su dispositivo m&oacute;vil.
              </p>
              <button className="btn-primary" style={{ width: '100%', height: '55px' }} onClick={() => downloadQR("qr-solicitudes-large")}>
                <i className="fa-solid fa-download"></i> Descargar Imagen para Imprimir
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedSolicitud && (
        <div className="modal-overlay" onClick={() => setSelectedSolicitud(null)}>
          <div className="solicitud-detail-card" onClick={e => e.stopPropagation()}>
            <div className="ticket-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <span className="p-badge" style={{ background: 'var(--sidebar-accent)', color: 'var(--p-indigo)', marginBottom: '10px', border: '1px solid var(--glass-border)' }}>
                    FOLIO #{selectedSolicitud.id.toString().padStart(5, '0')}
                  </span>
                  <h2 style={{ margin: 0, color: 'var(--p-navy)', fontSize: '24px' }}>Ficha de Solicitud</h2>
                  <div style={{ color: 'var(--p-slate)', fontSize: '13px', marginTop: '5px' }}>
                    Registrada el {new Date(selectedSolicitud.creado_en).toLocaleString('es-CL')}
                  </div>
                </div>
                <button className="action-button-glass" onClick={() => setSelectedSolicitud(null)} style={{ background: 'var(--sidebar-accent)' }}>
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>

            <div className="ticket-body">
              <div className="info-grid-ticket">
                <div className="ticket-section">
                  <span className="ticket-label">Funcionario</span>
                  <div className="ticket-value">{selectedSolicitud.usuario_nombre}</div>
                  <div style={{ color: 'var(--p-slate)', fontSize: '12px' }}>{selectedSolicitud.usuario_rut}</div>
                </div>
                <div className="ticket-section">
                  <span className="ticket-label">Tipo de Trámite</span>
                  <div className="ticket-value" style={{ color: 'var(--p-blue)', fontWeight: '700' }}>
                    {(selectedSolicitud.tipo_solicitud === 'Requerimiento' ? 'Nuevo Equipo' : (selectedSolicitud.tipo_solicitud || 'Solicitud de Equipo'))}
                  </div>
                </div>
              </div>

              <div className="ticket-section">
                <span className="ticket-label">Equipo Solicitado / Afectado</span>
                <div className="ticket-value" style={{ background: 'var(--sidebar-accent)', padding: '12px', borderRadius: '10px', border: '1px solid var(--glass-border)' }}>
                  <i className="fa-solid fa-laptop" style={{ marginRight: '10px', color: 'var(--p-blue)' }}></i>
                  {selectedSolicitud.equipo_solicitado}
                </div>
                {selectedSolicitud.datos_extra && (
                  <div className="extra-data-tag">
                    <i className="fa-solid fa-circle-info"></i>
                    {(() => {
                      try {
                        const d = typeof selectedSolicitud.datos_extra === 'string' ? JSON.parse(selectedSolicitud.datos_extra) : selectedSolicitud.datos_extra;
                        let infoStr = [];
                        if (d.destino) infoStr.push(`Destino: ${d.destino}`);
                        if (d.destinatario_prestamo) infoStr.push(`Beneficiario: ${d.destinatario_prestamo}`);
                        if (d.fecha_devolucion) infoStr.push(`Retorno Estimado: ${new Date(d.fecha_devolucion).toLocaleDateString()}`);
                        return infoStr.length > 0 ? infoStr.join(' | ') : 'Sin datos adicionales';
                      } catch(e) { return 'Error al leer detalles'; }
                    })()}
                  </div>
                )}
                {(() => {
                  try {
                    const d = typeof selectedSolicitud.datos_extra === 'string' ? JSON.parse(selectedSolicitud.datos_extra) : (selectedSolicitud.datos_extra || {});
                    if (d.equipo_asignado) {
                      return (
                        <div className="ticket-value" style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.3)', marginTop: '10px' }}>
                          <i className="fa-solid fa-check-double" style={{ marginRight: '10px', color: 'var(--p-emerald)' }}></i>
                          <strong style={{ color: 'var(--p-emerald)' }}>Equipo Entregado: </strong> <span style={{ color: 'var(--p-navy)' }}>{d.equipo_asignado}</span>
                        </div>
                      );
                    }
                  } catch(e) {}
                  return null;
                })()}
              </div>

              <div className="ticket-section">
                <span className="ticket-label">{selectedSolicitud.tipo_solicitud === 'Extravío' || selectedSolicitud.tipo_solicitud === 'Sustracción' ? 'Relato de los Hechos' : 'Motivo de la Solicitud'}</span>
                <div className="motivo-box-premium" style={{ color: 'var(--p-navy)', background: 'var(--sidebar-accent)', border: '1px solid var(--glass-border)' }}>
                  {selectedSolicitud.motivo}
                </div>
              </div>

              <div style={{ marginTop: '40px', display: 'flex', gap: '15px' }}>
                {activeTab === 'pendientes' && userRole === 'jefatura' && (
                  <>
                    <button className="btn-primary" style={{ flex: 1, background: 'var(--p-emerald)' }} onClick={() => { handleUpdateStatus(selectedSolicitud.id, 'Aprobada por Jefatura', '¿Aprobar solicitud?'); setSelectedSolicitud(null); }}>
                      <i className="fa-solid fa-check"></i> Autorizar Trámite
                    </button>
                    <button className="btn-secondary" style={{ flex: 1, border: '1px solid var(--p-rose)', color: 'var(--p-rose)' }} onClick={() => { handleUpdateStatus(selectedSolicitud.id, 'Rechazada', '¿Rechazar solicitud?'); setSelectedSolicitud(null); }}>
                      <i className="fa-solid fa-xmark"></i> Rechazar
                    </button>
                  </>
                )}
                {activeTab === 'pendientes' && (userRole === 'admin' || userRole === 'superadmin') && (
                  <>
                    <button className="btn-primary" style={{ flex: 1 }} onClick={() => { handleUpdateStatus(selectedSolicitud.id, 'Aprobada por Jefatura', '¿Aprobar solicitud para despacho?'); setSelectedSolicitud(null); }}>
                      <i className="fa-solid fa-check"></i> Aprobar
                    </button>
                    <button className="btn-secondary" style={{ flex: 1, border: '1px solid var(--p-rose)', color: 'var(--p-rose)' }} onClick={() => { handleUpdateStatus(selectedSolicitud.id, 'Rechazada', '¿Rechazar solicitud?'); setSelectedSolicitud(null); }}>
                      <i className="fa-solid fa-xmark"></i> Rechazar
                    </button>
                  </>
                )}
                {activeTab === 'historial' && (
                   <button className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setSelectedSolicitud(null)}>
                     Cerrar Vista
                   </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Solicitudes;
